/**
 * CADViewer.jsx
 * 3D CAD model viewer supporting STL, OBJ, GLTF/GLB, STEP/STP (via occt-import-js).
 * F3D / F3Z / 3MF / unknown → download-only card.
 *
 * Includes in-browser measurement tool:
 *   – Click two points on the model surface to measure distance
 *   – Shows floating label with distance + ΔX/Y/Z breakdown
 *   – Multiple measurements, individual clear, clear all
 *
 * Usage:
 *   <CADViewer url="https://..." filename="part.stl" />
 *   isCADFile("part.step") → true
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls }  from 'three/examples/jsm/controls/OrbitControls.js';
import { STLLoader }      from 'three/examples/jsm/loaders/STLLoader.js';
import { OBJLoader }      from 'three/examples/jsm/loaders/OBJLoader.js';
import { GLTFLoader }     from 'three/examples/jsm/loaders/GLTFLoader.js';

// ─── Firebase Storage bytes fetcher ──────────────────────────────────────────
async function fetchFileBytes(url) {
  try {
    const m = url.match(/\/v0\/b\/([^/]+)\/o\/([^?]+)/);
    if (m) {
      const bucket = m[1];
      const path   = decodeURIComponent(m[2]);
      const { getStorage, ref, getBytes } = await import('firebase/storage');
      return await getBytes(ref(getStorage(), `gs://${bucket}/${path}`));
    }
  } catch (_) {}
  try {
    const res = await fetch(url, { mode: 'cors', cache: 'no-store' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.arrayBuffer();
  } catch (_) {}
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'arraybuffer';
    xhr.onload  = () => xhr.status >= 200 && xhr.status < 300
      ? resolve(xhr.response)
      : reject(new Error(`XHR ${xhr.status}`));
    xhr.onerror = () => reject(new Error('Failed to load file. Check Firebase Storage CORS config.'));
    xhr.send();
  });
}

// ─── Extension helpers ────────────────────────────────────────────────────────
const CAD_EXTS   = new Set(['stl','obj','gltf','glb','step','stp','iges','igs','f3d','f3z','3mf','ply']);
const THREE_EXTS = new Set(['stl','obj','gltf','glb','ply']);
const STEP_EXTS  = new Set(['step','stp','iges','igs']);

export function getFileExt(filename) {
  if (!filename) return '';
  return filename.toLowerCase().split('.').pop();
}
export function isCADFile(filename) {
  return CAD_EXTS.has(getFileExt(filename));
}

// ─── Theme ────────────────────────────────────────────────────────────────────
const C = {
  bg: '#070a0f', panel: '#0c0f18', accent: '#0e9cc0',
  border: '#1e2636', dim: '#445566', text: '#dde9f0',
  red: '#f43f5e', green: '#10b981', yellow: '#f59e0b',
};

// ─── Measurement colours ──────────────────────────────────────────────────────
const MEASURE_COLOR  = 0xfbbf24;   // amber — sphere + line in Three.js
const MEASURE_ACTIVE = 0xff6b6b;   // first point pending second click
const LABEL_BG       = 'rgba(8,12,20,0.85)';
const LABEL_FG       = '#fbbf24';
const LABEL_DIM      = '#94a3b8';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtDist(d) {
  return d < 0.01 ? `${(d * 1000).toFixed(3)} µm`
       : d < 10   ? `${d.toFixed(3)} mm`
       : d < 1000 ? `${d.toFixed(2)} mm`
                  : `${(d / 1000).toFixed(4)} m`;
}

let _measureId = 0;
let _circleId  = 0;

// ─── Circle fitting math ──────────────────────────────────────────────────────
function _det3(a) {
  return a[0][0]*(a[1][1]*a[2][2]-a[1][2]*a[2][1])
        -a[0][1]*(a[1][0]*a[2][2]-a[1][2]*a[2][0])
        +a[0][2]*(a[1][0]*a[2][1]-a[1][1]*a[2][0]);
}
function _solve3(M, b) {
  const D = _det3(M);
  if (Math.abs(D) < 1e-14) return null;
  return [
    _det3([[b[0],M[0][1],M[0][2]],[b[1],M[1][1],M[1][2]],[b[2],M[2][1],M[2][2]]]) / D,
    _det3([[M[0][0],b[0],M[0][2]],[M[1][0],b[1],M[1][2]],[M[2][0],b[2],M[2][2]]]) / D,
    _det3([[M[0][0],M[0][1],b[0]],[M[1][0],M[1][1],b[1]],[M[2][0],M[2][1],b[2]]]) / D,
  ];
}
// Algebraic least-squares circle fit. Returns {cx,cy,r} or null.
function fitCircle2D(pts) {
  const n = pts.length;
  if (n < 4) return null;
  let sX=0,sY=0,sX2=0,sY2=0,sXY=0,sX3=0,sY3=0,sX2Y=0,sXY2=0;
  for (const [x,y] of pts){sX+=x;sY+=y;sX2+=x*x;sY2+=y*y;sXY+=x*y;sX3+=x*x*x;sY3+=y*y*y;sX2Y+=x*x*y;sXY2+=x*y*y;}
  const sol = _solve3([[sX2,sXY,sX],[sXY,sY2,sY],[sX,sY,n]],[sX3+sXY2,sX2Y+sY3,sX2+sY2]);
  if (!sol) return null;
  const cx=sol[0]/2, cy=sol[1]/2, r=Math.sqrt(cx*cx+cy*cy+sol[2]);
  return (isFinite(r) && r>0) ? {cx,cy,r} : null;
}

// Chain boundary edges (count===1) into ordered closed loops.
// edgeMap: Map<key, {count, va:Vector3, vb:Vector3}>
// Returns array of loops, each an ordered array of Vector3 positions.
function chainBoundaryEdges(edgeMap) {
  const PREC = 10000;
  function vk(v){ return `${Math.round(v.x*PREC)},${Math.round(v.y*PREC)},${Math.round(v.z*PREC)}`; }
  const adj   = new Map(); // vkey → [neighborKey,…]
  const ptOf  = new Map(); // vkey → Vector3
  for (const [,d] of edgeMap) {
    if (d.count !== 1) continue;
    const ka=vk(d.va), kb=vk(d.vb);
    ptOf.set(ka,d.va.clone()); ptOf.set(kb,d.vb.clone());
    if(!adj.has(ka)) adj.set(ka,[]);
    if(!adj.has(kb)) adj.set(kb,[]);
    adj.get(ka).push(kb); adj.get(kb).push(ka);
  }
  const visited = new Set();
  const loops   = [];
  for (const startKey of adj.keys()) {
    if (visited.has(startKey)) continue;
    const loop=[];
    let cur=startKey, prev=null;
    while (cur && !visited.has(cur)) {
      visited.add(cur);
      loop.push(ptOf.get(cur).clone());
      const nbrs=(adj.get(cur)||[]).filter(k=>k!==prev);
      prev=cur;
      cur=nbrs.find(k=>!visited.has(k))??null;
    }
    if (loop.length >= 4) loops.push(loop);
  }
  return loops;
}

// Classify one closed loop as 'circle' or 'polygon'.
// Returns {type:'circle', cx,cy,r,center3D,u,v} or {type:'polygon'}.
function classifyLoop(loop, hitPoint, u, v) {
  const pts2D = loop.map(p=>[p.clone().sub(hitPoint).dot(u), p.clone().sub(hitPoint).dot(v)]);
  const circle = fitCircle2D(pts2D);
  if (!circle) return { type:'polygon' };
  const {cx,cy,r} = circle;
  // Angular coverage – gap must be < 60° (full arc)
  const angles = pts2D.map(([x,y])=>Math.atan2(y-cy,x-cx)).sort((a,b)=>a-b);
  const maxGap = angles.reduce((mg,a,i)=>Math.max(mg, i>0?a-angles[i-1]:0),
                               angles[0]+Math.PI*2-angles[angles.length-1]);
  // Residuals
  const res = pts2D.map(([x,y])=>Math.abs(Math.hypot(x-cx,y-cy)-r));
  const avgRes = res.reduce((a,b)=>a+b,0)/res.length;
  const maxRes = Math.max(...res);
  // STRICT: avg<4%, max<10%, gap<60° AND at least 12 distinct angular samples
  if (avgRes<r*0.04 && maxRes<r*0.10 && maxGap<Math.PI/3 && loop.length>=12) {
    const center3D = hitPoint.clone().addScaledVector(u,cx).addScaledVector(v,cy);
    return { type:'circle', cx, cy, r, center3D, u:u.clone(), v:v.clone() };
  }
  return { type:'polygon' };
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function CADViewer({ url, filename, height = 440 }) {
  const mountRef       = useRef(null);
  const overlayRef     = useRef(null);   // 2D canvas for measurement labels
  const viewcubeRef    = useRef(null);   // small orientation cube canvas
  const rendererRef    = useRef(null);
  const sceneRef       = useRef(null);
  const cameraRef      = useRef(null);
  const controlsRef    = useRef(null);
  const rootObjRef     = useRef(null);
  const rafRef         = useRef(null);
  const snapTargetRef  = useRef(null);   // {pos:Vector3} camera snap animation

  // Measurement refs (accessed inside Three.js loop / event handlers)
  const measureModeRef      = useRef(false);
  const activePtRef         = useRef(null);    // first picked point (Vector3) pending 2nd
  const measurementsRef     = useRef([]);      // [{id,p1,p2,dist,dx,dy,dz,objs:[]}]
  const markerRadiusRef     = useRef(0.5);
  const pointerDownRef      = useRef(null);
  const measureSceneRef     = useRef([]);      // THREE objects to remove on clear

  // React state
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [wireframe,    setWireframe]    = useState(false);
  const [info,         setInfo]         = useState(null);
  const [measureMode,  setMeasureMode]  = useState(false);
  const [measurements, setMeasurements] = useState([]);   // mirrors measurementsRef for render
  const [activePt,     setActivePt]     = useState(null); // first point picked

  const [circleMode,   setCircleMode]   = useState(false);
  const [circleMeasurements, setCircleMeasurements] = useState([]);
  const [circleNoHit,  setCircleNoHit]  = useState(false);
  const circleModeRef          = useRef(false);
  const circleMeasurementsRef  = useRef([]);

  // Face-to-face distance mode
  const [faceDistMode,   setFaceDistMode]   = useState(false);
  const [facePending,    setFacePending]     = useState(false); // waiting for 2nd face
  const [faceDistMeasurements, setFaceDistMeasurements] = useState([]);
  const [faceDistMsg,    setFaceDistMsg]     = useState(null); // flash message
  const faceDistModeRef  = useRef(false);
  const facePendingRef   = useRef(null);     // {normal, point, markerObjs}
  const faceDistMeasRef  = useRef([]);

  const ext = getFileExt(filename);

  // ── keep mode refs in sync ─────────────────────────────────────────────────
  useEffect(() => { measureModeRef.current = measureMode; }, [measureMode]);
  useEffect(() => { circleModeRef.current   = circleMode;   }, [circleMode]);
  useEffect(() => { faceDistModeRef.current = faceDistMode; }, [faceDistMode]);

  // ── fit camera ─────────────────────────────────────────────────────────────
  const fitCamera = useCallback((box3) => {
    const cam = cameraRef.current; const ctrl = controlsRef.current;
    if (!cam || !ctrl) return;
    const size = new THREE.Vector3(); const center = new THREE.Vector3();
    box3.getSize(size); box3.getCenter(center);
    const maxDim = Math.max(size.x, size.y, size.z);
    const fovRad = cam.fov * (Math.PI / 180);
    const dist   = (maxDim / 2) / Math.tan(fovRad / 2) * 1.8;
    cam.position.set(center.x + dist * 0.6, center.y + dist * 0.5, center.z + dist);
    cam.far  = dist * 10; cam.near = dist * 0.001;
    cam.lookAt(center); cam.updateProjectionMatrix();
    ctrl.target.copy(center); ctrl.update();
  }, []);

  // ── wireframe ──────────────────────────────────────────────────────────────
  const applyWireframe = useCallback((wf) => {
    if (!rootObjRef.current) return;
    rootObjRef.current.traverse(child => {
      if (!child.isMesh) return;
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      mats.forEach(m => { if (m) m.wireframe = wf; });
    });
  }, []);
  useEffect(() => { applyWireframe(wireframe); }, [wireframe, applyWireframe]);

  // ── draw measurement labels on the 2D overlay canvas ──────────────────────
  const drawMeasureOverlay = useCallback(() => {
    const oc = overlayRef.current;
    const cam = cameraRef.current;
    if (!oc || !cam) return;
    const ctx = oc.getContext('2d');
    ctx.clearRect(0, 0, oc.width, oc.height);

    const W = oc.width, H = oc.height;

    function project(v3) {
      const v = v3.clone().project(cam);
      return { x: (v.x + 1) / 2 * W, y: (-v.y + 1) / 2 * H };
    }

    // draw completed measurements
    for (const m of measurementsRef.current) {
      const sp1 = project(m.p1);
      const sp2 = project(m.p2);
      const mid = { x: (sp1.x + sp2.x) / 2, y: (sp1.y + sp2.y) / 2 };

      // dot markers
      [sp1, sp2].forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, 5, 0, Math.PI * 2);
        ctx.fillStyle = '#fbbf24';
        ctx.fill();
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      // line
      ctx.beginPath();
      ctx.moveTo(sp1.x, sp1.y);
      ctx.lineTo(sp2.x, sp2.y);
      ctx.strokeStyle = '#fbbf24cc';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([5, 4]);
      ctx.stroke();
      ctx.setLineDash([]);

      // label box
      ctx.font = 'bold 12px monospace';
      const mainText = fmtDist(m.dist);
      const mw = ctx.measureText(mainText).width + 16;
      const bx = mid.x - mw / 2, by = mid.y - 14;
      ctx.fillStyle = LABEL_BG;
      ctx.beginPath();
      ctx.roundRect(bx - 2, by - 2, mw + 4, 20, 4);
      ctx.fill();
      ctx.fillStyle = LABEL_FG;
      ctx.fillText(mainText, bx + 8, by + 12);

      // small delta text below label
      ctx.font = '9px monospace';
      const dText = `Δ${m.dx.toFixed(2)}  ${m.dy.toFixed(2)}  ${m.dz.toFixed(2)}`;
      const dw = ctx.measureText(dText).width + 10;
      ctx.fillStyle = LABEL_BG;
      ctx.beginPath();
      ctx.roundRect(mid.x - dw / 2 - 2, by + 20, dw + 4, 14, 3);
      ctx.fill();
      ctx.fillStyle = LABEL_DIM;
      ctx.fillText(dText, mid.x - dw / 2 + 5, by + 31);
    }

    // draw face-distance labels
    for (const m of faceDistMeasRef.current) {
      const sp1 = project(m.p1), sp2 = project(m.p2);
      const mid = { x:(sp1.x+sp2.x)/2, y:(sp1.y+sp2.y)/2 };
      ctx.beginPath(); ctx.moveTo(sp1.x,sp1.y); ctx.lineTo(sp2.x,sp2.y);
      ctx.strokeStyle='#a855f7cc'; ctx.lineWidth=1.5; ctx.setLineDash([6,4]); ctx.stroke(); ctx.setLineDash([]);
      [sp1,sp2].forEach(p=>{
        ctx.beginPath(); ctx.arc(p.x,p.y,5,0,Math.PI*2);
        ctx.fillStyle='#a855f7'; ctx.fill(); ctx.strokeStyle='#000'; ctx.lineWidth=1; ctx.stroke();
      });
      ctx.font='bold 12px monospace';
      const txt=`⟂ ${fmtDist(m.dist)}`;
      const tw=ctx.measureText(txt).width+16;
      const bx=mid.x-tw/2, by=mid.y-14;
      ctx.fillStyle='rgba(8,12,20,0.88)'; ctx.beginPath(); ctx.roundRect(bx-2,by-2,tw+4,20,4); ctx.fill();
      ctx.fillStyle='#a855f7'; ctx.fillText(txt,bx+8,by+12);
    }

    // draw circle measurement labels
    for (const m of circleMeasurementsRef.current) {
      const sc = project(m.center);
      // center dot
      ctx.beginPath(); ctx.arc(sc.x, sc.y, 5, 0, Math.PI*2);
      ctx.fillStyle = '#22d3ee'; ctx.fill();
      ctx.strokeStyle='#000'; ctx.lineWidth=1; ctx.stroke();
      // label: R and Ø
      const rText = `R ${fmtDist(m.radius)}`;
      const dText = `Ø ${fmtDist(m.radius*2)}`;
      ctx.font = 'bold 12px monospace';
      const rw = ctx.measureText(rText).width + 16;
      const bx = sc.x + 10, by = sc.y - 20;
      ctx.fillStyle = 'rgba(8,12,20,0.88)';
      ctx.beginPath(); ctx.roundRect(bx-2, by-2, rw+4, 34, 4); ctx.fill();
      ctx.fillStyle = '#22d3ee'; ctx.fillText(rText, bx+8, by+12);
      ctx.font = '10px monospace';
      ctx.fillStyle = '#94a3b8'; ctx.fillText(dText, bx+8, by+27);
    }

    // draw active (first point placed, waiting for second)
    if (activePtRef.current) {
      const sp = project(activePtRef.current);
      ctx.beginPath();
      ctx.arc(sp.x, sp.y, 6, 0, Math.PI * 2);
      ctx.fillStyle = '#ff6b6b';
      ctx.fill();
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // pulse ring
      ctx.beginPath();
      ctx.arc(sp.x, sp.y, 10, 0, Math.PI * 2);
      ctx.strokeStyle = '#ff6b6b88';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }, []);

  // ── Raycasting helpers ─────────────────────────────────────────────────────
  function raycastModel(clientX, clientY) {
    const renderer = rendererRef.current;
    const camera   = cameraRef.current;
    const scene    = sceneRef.current;
    if (!renderer || !camera || !scene) return null;

    const rect   = renderer.domElement.getBoundingClientRect();
    const mouse  = new THREE.Vector2(
      ((clientX - rect.left)  / rect.width)  * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, camera);

    const meshes = [];
    scene.traverse(c => { if (c.isMesh && c.userData._marker !== true) meshes.push(c); });
    const hits = raycaster.intersectObjects(meshes, true);
    return hits.length > 0 ? hits[0].point.clone() : null;
  }

  function addSphereToScene(pos, color, radius) {
    const scene = sceneRef.current;
    if (!scene) return null;
    const geo  = new THREE.SphereGeometry(radius, 12, 12);
    const mat  = new THREE.MeshBasicMaterial({ color, depthTest: false });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.userData._marker = true;
    mesh.position.copy(pos);
    scene.add(mesh);
    return mesh;
  }

  function addLineToScene(p1, p2, color) {
    const scene = sceneRef.current;
    if (!scene) return null;
    const geo  = new THREE.BufferGeometry().setFromPoints([p1, p2]);
    const mat  = new THREE.LineBasicMaterial({ color, depthTest: false });
    const line = new THREE.Line(geo, mat);
    line.userData._marker = true;
    scene.add(line);
    return line;
  }

  function removeSceneObjs(objs) {
    const scene = sceneRef.current;
    if (!scene) return;
    for (const o of objs) {
      scene.remove(o);
      o.geometry?.dispose();
      if (Array.isArray(o.material)) o.material.forEach(m => m.dispose());
      else o.material?.dispose();
    }
  }

  // ── Handle a measure click (called after confirming it's a click not drag) ──
  const handleMeasureClick = useCallback((clientX, clientY) => {
    const pt = raycastModel(clientX, clientY);
    if (!pt) return;
    const r = markerRadiusRef.current;

    if (!activePtRef.current) {
      // First point
      const sphere = addSphereToScene(pt, MEASURE_ACTIVE, r);
      activePtRef.current = pt;
      measureSceneRef.current = sphere ? [sphere] : [];
      setActivePt({ x: pt.x, y: pt.y, z: pt.z });
    } else {
      // Second point — finalise measurement
      const p1 = activePtRef.current;
      const p2 = pt;
      const dx = Math.abs(p2.x - p1.x);
      const dy = Math.abs(p2.y - p1.y);
      const dz = Math.abs(p2.z - p1.z);
      const dist = p1.distanceTo(p2);

      // Replace active sphere with finished colour
      removeSceneObjs(measureSceneRef.current);
      measureSceneRef.current = [];

      const s1   = addSphereToScene(p1, MEASURE_COLOR, r);
      const s2   = addSphereToScene(p2, MEASURE_COLOR, r);
      const line = addLineToScene(p1, p2, MEASURE_COLOR);
      const objs = [s1, s2, line].filter(Boolean);

      const id = ++_measureId;
      const m  = { id, p1: p1.clone(), p2: p2.clone(), dist, dx, dy, dz, objs };
      measurementsRef.current = [...measurementsRef.current, m];
      setMeasurements(prev => [...prev, { id, dist, dx, dy, dz }]);

      activePtRef.current = null;
      setActivePt(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Clear one measurement ──────────────────────────────────────────────────
  const clearMeasurement = useCallback((id) => {
    const m = measurementsRef.current.find(x => x.id === id);
    if (m) removeSceneObjs(m.objs);
    measurementsRef.current = measurementsRef.current.filter(x => x.id !== id);
    setMeasurements(prev => prev.filter(x => x.id !== id));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Clear all measurements ─────────────────────────────────────────────────
  const clearAllMeasurements = useCallback(() => {
    for (const m of measurementsRef.current) removeSceneObjs(m.objs);
    removeSceneObjs(measureSceneRef.current);
    measurementsRef.current = [];
    measureSceneRef.current = [];
    activePtRef.current = null;
    setMeasurements([]);
    setActivePt(null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Full raycast (returns hit object for circle detection) ────────────────
  function raycastFull(clientX, clientY) {
    const renderer = rendererRef.current;
    const camera   = cameraRef.current;
    const scene    = sceneRef.current;
    if (!renderer || !camera || !scene) return null;
    const rect   = renderer.domElement.getBoundingClientRect();
    const mouse  = new THREE.Vector2(
      ((clientX - rect.left) / rect.width)  * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    const ray = new THREE.Raycaster();
    ray.setFromCamera(mouse, camera);
    const meshes = [];
    scene.traverse(c => { if (c.isMesh && c.userData._marker !== true) meshes.push(c); });
    const hits = ray.intersectObjects(meshes, true);
    return hits.length > 0 ? hits[0] : null;
  }

  // ── Detect circle: proper topology → loop chaining → strict classification ──
  // Returns {center, normal, radius, u, v} or null when no real circle found.
  function detectCircle(hit) {
    const { point: hitPoint, face, object } = hit;
    const geo = object.geometry;
    const mat = object.matrixWorld;
    const pos = geo.attributes.position;
    const idx = geo.index;
    if (!pos || !face) return null;

    const nTri = idx ? idx.count / 3 : pos.count / 3;
    const worldNormal = face.normal.clone().transformDirection(mat).normalize();
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(worldNormal, hitPoint);

    // Very tight plane tolerance so we only grab truly coplanar faces
    const planeTol  = Math.max(markerRadiusRef.current * 0.8, 0.005);
    const normalCos = Math.cos(8 * Math.PI / 180); // 8° tolerance

    const PREC = 10000;
    function vk(v){ return `${Math.round(v.x*PREC)},${Math.round(v.y*PREC)},${Math.round(v.z*PREC)}`; }
    function ek(a,b){ const ka=vk(a),kb=vk(b); return ka<kb?`${ka}|${kb}`:`${kb}|${ka}`; }

    // ── Collect all coplanar triangles (same plane as hit face) ──
    const edgeMap = new Map();
    let included  = 0;

    for (let fi = 0; fi < nTri; fi++) {
      const i0=idx?idx.getX(fi*3):fi*3, i1=idx?idx.getX(fi*3+1):fi*3+1, i2=idx?idx.getX(fi*3+2):fi*3+2;
      const a=new THREE.Vector3().fromBufferAttribute(pos,i0).applyMatrix4(mat);
      const b=new THREE.Vector3().fromBufferAttribute(pos,i1).applyMatrix4(mat);
      const c=new THREE.Vector3().fromBufferAttribute(pos,i2).applyMatrix4(mat);
      const e1=b.clone().sub(a),e2=c.clone().sub(a);
      const fn=e1.cross(e2); if(fn.length()<1e-10) continue; fn.normalize();
      if (Math.abs(fn.dot(worldNormal)) < normalCos) continue;
      if (Math.abs(plane.distanceToPoint(a))>planeTol) continue;
      if (Math.abs(plane.distanceToPoint(b))>planeTol) continue;
      if (Math.abs(plane.distanceToPoint(c))>planeTol) continue;
      included++;
      for (const [va,vb] of [[a,b],[b,c],[c,a]]) {
        const key=ek(va,vb);
        if(!edgeMap.has(key)) edgeMap.set(key,{count:0,va:va.clone(),vb:vb.clone()});
        edgeMap.get(key).count++;
      }
    }
    if (included < 3) return null;

    // ── Chain boundary edges into proper closed loops ──
    const loops = chainBoundaryEdges(edgeMap);
    if (loops.length === 0) return null;

    // ── 2D basis in the plane ──
    const n = worldNormal;
    const tmp = Math.abs(n.y)<0.9 ? new THREE.Vector3(0,1,0) : new THREE.Vector3(1,0,0);
    const u = new THREE.Vector3().crossVectors(n,tmp).normalize();
    const v = new THREE.Vector3().crossVectors(n,u).normalize();

    // ── Try each loop, closest-to-hit-point first ──
    const sorted = loops
      .map(loop=>({ loop, dist: loop.reduce((acc,p)=>acc+p.distanceTo(hitPoint),0)/loop.length }))
      .sort((a,b)=>a.dist-b.dist);

    for (const { loop } of sorted) {
      const result = classifyLoop(loop, hitPoint, u, v);
      if (result.type === 'circle') {
        return { center: result.center3D, normal: worldNormal, radius: result.r, u: result.u, v: result.v };
      }
    }
    return null;   // no loop classified as a genuine circle
  }

  // ── Detect cylinder: when clicking on curved surface find axis + radius ──────
  function detectCylinder(hit) {
    const { point: hitPoint, face, object } = hit;
    const geo = object.geometry;
    const mat = object.matrixWorld;
    const pos = geo.attributes.position;
    const idx = geo.index;
    if (!pos || !face) return null;

    const nTri = idx ? idx.count/3 : pos.count/3;
    const hitNormal = face.normal.clone().transformDirection(mat).normalize();
    const searchR   = markerRadiusRef.current * 25;

    // Collect centroids + normals of nearby triangles
    const samples = [];
    for (let fi=0; fi<nTri; fi++) {
      const i0=idx?idx.getX(fi*3):fi*3, i1=idx?idx.getX(fi*3+1):fi*3+1, i2=idx?idx.getX(fi*3+2):fi*3+2;
      const a=new THREE.Vector3().fromBufferAttribute(pos,i0).applyMatrix4(mat);
      const b=new THREE.Vector3().fromBufferAttribute(pos,i1).applyMatrix4(mat);
      const c=new THREE.Vector3().fromBufferAttribute(pos,i2).applyMatrix4(mat);
      const cen=a.clone().add(b).add(c).divideScalar(3);
      if(cen.distanceTo(hitPoint)>searchR) continue;
      const e1=b.clone().sub(a),e2=c.clone().sub(a);
      const fn=e1.cross(e2); if(fn.length()<1e-10) continue; fn.normalize();
      // Only grab faces that point in similar-ish direction (within 45°)
      if(fn.dot(hitNormal) < 0.7) continue;
      samples.push({cen,normal:fn});
    }
    if (samples.length < 8) return null;

    // Candidate cylinder axes = cross-products of distant normal pairs
    const axisCandidates = [];
    const step = Math.max(1, Math.floor(samples.length/12));
    for (let i=0; i<samples.length; i+=step) {
      for (let j=i+step; j<samples.length; j+=step) {
        const ax=samples[i].normal.clone().cross(samples[j].normal);
        if(ax.length()>0.08){ ax.normalize(); axisCandidates.push(ax); }
      }
    }
    if(axisCandidates.length===0) return null;

    // For each candidate axis, project sample centroids perp to axis and fit circle
    let best = null;
    for (const axis of axisCandidates) {
      const tmp2=Math.abs(axis.y)<0.9?new THREE.Vector3(0,1,0):new THREE.Vector3(1,0,0);
      const pu=new THREE.Vector3().crossVectors(axis,tmp2).normalize();
      const pv=new THREE.Vector3().crossVectors(axis,pu).normalize();
      const pts2D=samples.map(({cen})=>{
        const d=cen.clone().sub(hitPoint);
        const along=axis.clone().multiplyScalar(d.dot(axis));
        const perp=d.clone().sub(along);
        return [perp.dot(pu),perp.dot(pv)];
      });
      const circle=fitCircle2D(pts2D); if(!circle) continue;
      const {cx,cy,r}=circle;
      const res=pts2D.map(([x,y])=>Math.abs(Math.hypot(x-cx,y-cy)-r));
      const avgRes=res.reduce((a,b)=>a+b,0)/res.length;
      if(avgRes<r*0.07 && (!best||avgRes<best.avgRes)) {
        const center=hitPoint.clone().addScaledVector(pu,cx).addScaledVector(pv,cy);
        best={radius:r,axis,center,u:pu,v:pv,avgRes};
      }
    }
    return best;
  }

  // ── Draw circle in 3D scene (returns array of Three.js objects) ────────────
  function addCircleToScene(center, u_axis, v_axis, radius, color) {
    const scene = sceneRef.current; if (!scene) return [];
    const N  = 128;
    const pts = [];
    for (let i = 0; i <= N; i++) {
      const a = (i/N)*Math.PI*2;
      pts.push(center.clone().addScaledVector(u_axis, Math.cos(a)*radius).addScaledVector(v_axis, Math.sin(a)*radius));
    }
    const cGeo = new THREE.BufferGeometry().setFromPoints(pts);
    const cMat = new THREE.LineBasicMaterial({ color, depthTest:false });
    const ring  = new THREE.Line(cGeo, cMat); ring.userData._marker=true; scene.add(ring);

    // Radius line (center → edge at 0°)
    const edgePt = center.clone().addScaledVector(u_axis, radius);
    const rGeo = new THREE.BufferGeometry().setFromPoints([center.clone(), edgePt]);
    const rMat = new THREE.LineBasicMaterial({ color, depthTest:false });
    const rLine = new THREE.Line(rGeo, rMat); rLine.userData._marker=true; scene.add(rLine);

    const dot = addSphereToScene(center, color, markerRadiusRef.current * 0.9);
    return [ring, rLine, dot].filter(Boolean);
  }

  // ── Handle circle click — tries flat-circle then cylinder fallback ─────────
  const handleCircleClick = useCallback((clientX, clientY) => {
    const hit = raycastFull(clientX, clientY);
    if (!hit) { setCircleNoHit(true); setTimeout(()=>setCircleNoHit(false), 2500); return; }

    // Try flat circular face first
    let result = detectCircle(hit);
    let isCylinder = false;
    if (!result) {
      // Try cylindrical surface
      const cyl = detectCylinder(hit);
      if (cyl) { result = cyl; isCylinder = true; }
    }

    if (!result) { setCircleNoHit(true); setTimeout(()=>setCircleNoHit(false), 2500); return; }

    const { center, radius, u, v } = result;
    const color = isCylinder ? 0xa78bfa : 0x22d3ee; // purple for cylinder, cyan for circle
    const objs  = addCircleToScene(center, u, v, radius, color);
    const id    = ++_circleId;
    const m     = { id, center:center.clone(), radius, u:u.clone(), v:v.clone(), objs, isCylinder };
    circleMeasurementsRef.current = [...circleMeasurementsRef.current, m];
    setCircleMeasurements(prev => [...prev, { id, radius, isCylinder }]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Handle face-to-face distance click ────────────────────────────────────
  const handleFaceDistClick = useCallback((clientX, clientY) => {
    const hit = raycastFull(clientX, clientY);
    if (!hit) return;
    const worldNormal = hit.face.normal.clone().transformDirection(hit.object.matrixWorld).normalize();
    const hitPt = hit.point.clone();

    if (!facePendingRef.current) {
      // First face — store it and show a pending marker
      const m1 = addSphereToScene(hitPt, 0xa855f7, markerRadiusRef.current * 1.1);
      facePendingRef.current = { normal: worldNormal, point: hitPt, markerObjs: [m1].filter(Boolean) };
      setFacePending(true);
    } else {
      // Second face — compute distance
      const f1 = facePendingRef.current;
      removeSceneObjs(f1.markerObjs);

      const dot = Math.abs(f1.normal.dot(worldNormal));

      if (dot > 0.97) {
        // Faces are parallel → perpendicular distance
        const dist = Math.abs(f1.normal.dot(hitPt.clone().sub(f1.point)));
        const s1   = addSphereToScene(f1.point, 0xa855f7, markerRadiusRef.current);
        const s2   = addSphereToScene(hitPt,    0xa855f7, markerRadiusRef.current);
        const ln   = addLineToScene(f1.point, hitPt, 0xa855f7);
        const objs = [s1,s2,ln].filter(Boolean);
        const id   = ++_measureId;
        const entry = { id, dist, p1:f1.point.clone(), p2:hitPt.clone(), objs };
        faceDistMeasRef.current = [...faceDistMeasRef.current, entry];
        setFaceDistMeasurements(prev=>[...prev, { id, dist }]);
        setFaceDistMsg(null);
      } else {
        const angleDeg = Math.acos(Math.min(dot,1)) * 180/Math.PI;
        setFaceDistMsg(`Faces are not parallel (angle between them: ${angleDeg.toFixed(1)}°) — select two parallel faces`);
        setTimeout(()=>setFaceDistMsg(null), 4000);
      }

      facePendingRef.current = null;
      setFacePending(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearFaceDist = useCallback((id) => {
    const m = faceDistMeasRef.current.find(x=>x.id===id);
    if (m) removeSceneObjs(m.objs);
    faceDistMeasRef.current = faceDistMeasRef.current.filter(x=>x.id!==id);
    setFaceDistMeasurements(prev=>prev.filter(x=>x.id!==id));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearAllFaceDist = useCallback(() => {
    for (const m of faceDistMeasRef.current) removeSceneObjs(m.objs);
    if (facePendingRef.current) { removeSceneObjs(facePendingRef.current.markerObjs); facePendingRef.current=null; }
    faceDistMeasRef.current = [];
    setFaceDistMeasurements([]);
    setFacePending(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Clear circle measurements ──────────────────────────────────────────────
  const clearCircle = useCallback((id) => {
    const m = circleMeasurementsRef.current.find(x=>x.id===id);
    if (m) removeSceneObjs(m.objs);
    circleMeasurementsRef.current = circleMeasurementsRef.current.filter(x=>x.id!==id);
    setCircleMeasurements(prev=>prev.filter(x=>x.id!==id));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearAllCircles = useCallback(() => {
    for (const m of circleMeasurementsRef.current) removeSceneObjs(m.objs);
    circleMeasurementsRef.current = [];
    setCircleMeasurements([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Pointer events for measure mode ───────────────────────────────────────
  // Register on mountRef (the container div) — it is available immediately,
  // unlike rendererRef.current which is populated asynchronously by the
  // Three.js setup effect.
  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    function onDown(e) {
      pointerDownRef.current = { x: e.clientX, y: e.clientY };
    }
    function onUp(e) {
      const d = pointerDownRef.current;
      if (!d) return;
      const dx = e.clientX - d.x, dy = e.clientY - d.y;
      if (Math.sqrt(dx * dx + dy * dy) < 6) {
        if      (measureModeRef.current)  handleMeasureClick(e.clientX, e.clientY);
        else if (circleModeRef.current)   handleCircleClick(e.clientX, e.clientY);
        else if (faceDistModeRef.current) handleFaceDistClick(e.clientX, e.clientY);
      }
      pointerDownRef.current = null;
    }

    container.addEventListener('pointerdown', onDown);
    container.addEventListener('pointerup', onUp);
    return () => {
      container.removeEventListener('pointerdown', onDown);
      container.removeEventListener('pointerup', onUp);
    };
  }, [handleMeasureClick, handleCircleClick, handleFaceDistClick]);

  // ── When measure mode is turned off, clear pending first point ────────────
  useEffect(() => {
    if (!measureMode) {
      removeSceneObjs(measureSceneRef.current);
      measureSceneRef.current = [];
      activePtRef.current = null;
      setActivePt(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureMode]);

  // ── Three.js setup + model load ────────────────────────────────────────────
  useEffect(() => {
    if (!url) return;
    const container = mountRef.current;
    if (!container) return;
    const W = container.clientWidth || 600;
    const H = height;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#080c14');
    sceneRef.current = scene;

    scene.add(new THREE.AmbientLight(0xffffff, 0.9));
    const sun = new THREE.DirectionalLight(0xffffff, 1.2);
    sun.position.set(5, 10, 7); scene.add(sun);
    const fill = new THREE.DirectionalLight(0xaabbff, 0.5);
    fill.position.set(-5, -3, -5); scene.add(fill);
    const back = new THREE.DirectionalLight(0xffffff, 0.3);
    back.position.set(0, -10, -5); scene.add(back);

    const grid = new THREE.GridHelper(200, 30, 0x1a2030, 0x111820);
    scene.add(grid);

    // Camera
    const camera = new THREE.PerspectiveCamera(45, W / H, 0.001, 100000);
    camera.position.set(0, 2, 5);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Overlay canvas (2D, for measurement labels)
    const oc = overlayRef.current;
    if (oc) { oc.width = W; oc.height = H; }

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.06;
    controls.screenSpacePanning = true;
    controlsRef.current = controls;

    // ── View-cube (orientation indicator, bottom-right) ───────────────────────
    const vcCanvas = viewcubeRef.current;
    const VCS = 110; // canvas size px
    let vcRenderer = null, vcScene = null, vcCamera = null, vcCube = null;

    if (vcCanvas) {
      vcRenderer = new THREE.WebGLRenderer({ canvas: vcCanvas, antialias: true, alpha: true });
      vcRenderer.setSize(VCS, VCS);
      vcRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      vcRenderer.setClearColor(0x000000, 0);

      vcScene  = new THREE.Scene();
      vcCamera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
      vcCamera.position.set(0, 0, 4.2);

      vcScene.add(new THREE.AmbientLight(0xffffff, 0.7));
      const vcSun = new THREE.DirectionalLight(0xffffff, 0.8);
      vcSun.position.set(2, 3, 4); vcScene.add(vcSun);

      // Per-face canvas textures: BoxGeometry order = [+X,-X,+Y,-Y,+Z,-Z]
      const faceDefs = [
        { label:'RIGHT', bg:'#7f1d1d', fg:'#fecaca' },
        { label:'LEFT',  bg:'#3b0764', fg:'#e9d5ff' },
        { label:'TOP',   bg:'#14532d', fg:'#bbf7d0' },
        { label:'BOT',   bg:'#1c1917', fg:'#a8a29e' },
        { label:'FRONT', bg:'#1e3a5f', fg:'#bae6fd' },
        { label:'BACK',  bg:'#172554', fg:'#818cf8' },
      ];
      const cubeMats = faceDefs.map(({ label, bg, fg }) => {
        const c = document.createElement('canvas'); c.width=128; c.height=128;
        const ctx2 = c.getContext('2d');
        // Fill + subtle radial highlight
        ctx2.fillStyle = bg; ctx2.fillRect(0,0,128,128);
        const grad = ctx2.createRadialGradient(64,64,10,64,64,80);
        grad.addColorStop(0,'rgba(255,255,255,0.12)'); grad.addColorStop(1,'rgba(0,0,0,0)');
        ctx2.fillStyle = grad; ctx2.fillRect(0,0,128,128);
        // Border
        ctx2.strokeStyle='rgba(255,255,255,0.25)'; ctx2.lineWidth=5;
        ctx2.strokeRect(3,3,122,122);
        // Label
        ctx2.fillStyle = fg; ctx2.font='bold 30px monospace';
        ctx2.textAlign='center'; ctx2.textBaseline='middle';
        ctx2.fillText(label, 64, 64);
        return new THREE.MeshPhongMaterial({ map: new THREE.CanvasTexture(c), shininess:60 });
      });
      vcCube = new THREE.Mesh(new THREE.BoxGeometry(2, 2, 2), cubeMats);
      vcScene.add(vcCube);

      // Axis lines (X=red, Y=green, Z=blue) extending beyond cube faces
      const axisSegs = [
        { pts:[[-1.3,0,0],[1.3,0,0]], color:0xef4444 }, // X
        { pts:[[0,-1.3,0],[0,1.3,0]], color:0x22c55e }, // Y
        { pts:[[0,0,-1.3],[0,0,1.3]], color:0x3b82f6 }, // Z
      ];
      for (const { pts, color } of axisSegs) {
        const g=new THREE.BufferGeometry().setFromPoints(pts.map(p=>new THREE.Vector3(...p)));
        const m=new THREE.LineBasicMaterial({ color, depthTest:false });
        vcScene.add(new THREE.Line(g,m));
      }
      // Axis letter sprites at the tips
      function axisSprite(label, pos, color) {
        const c2=document.createElement('canvas'); c2.width=64; c2.height=64;
        const ctx3=c2.getContext('2d');
        ctx3.fillStyle=color; ctx3.font='bold 44px monospace';
        ctx3.textAlign='center'; ctx3.textBaseline='middle';
        ctx3.fillText(label,32,32);
        const sp=new THREE.Sprite(new THREE.SpriteMaterial({ map:new THREE.CanvasTexture(c2), depthTest:false }));
        sp.position.set(...pos); sp.scale.set(0.7,0.7,1);
        return sp;
      }
      vcScene.add(axisSprite('X',[1.9,0,0],'#ef4444'));
      vcScene.add(axisSprite('Y',[0,1.9,0],'#22c55e'));
      vcScene.add(axisSprite('Z',[0,0,1.9],'#3b82f6'));

      // Click on view-cube face → snap main camera to that orientation
      vcCanvas.addEventListener('click', (ev) => {
        const rect2 = vcCanvas.getBoundingClientRect();
        const mx = ((ev.clientX-rect2.left)/VCS)*2-1;
        const my = -((ev.clientY-rect2.top)/VCS)*2+1;
        const ray2 = new THREE.Raycaster();
        ray2.setFromCamera(new THREE.Vector2(mx,my), vcCamera);
        const hits2 = ray2.intersectObject(vcCube, false);
        if (!hits2.length) return;
        // face.normal is in cube's local space. Since cube is rotated by camera.quaternion.invert(),
        // the world-direction in the main scene = faceNormal.applyQuaternion(camera.quaternion)
        const faceNorm = hits2[0].face.normal.clone().applyQuaternion(camera.quaternion);
        const dist2 = camera.position.distanceTo(controls.target);
        const snapPos = controls.target.clone().addScaledVector(faceNorm, dist2);
        snapTargetRef.current = { pos: snapPos, up: faceNorm.y > 0.95 ? new THREE.Vector3(0,0,-1) : new THREE.Vector3(0,1,0) };
      });
    }

    // Resize
    const ro = new ResizeObserver(() => {
      const w = container.clientWidth;
      camera.aspect = w / height;
      camera.updateProjectionMatrix();
      renderer.setSize(w, height);
      if (overlayRef.current) { overlayRef.current.width = w; overlayRef.current.height = height; }
    });
    ro.observe(container);

    // Animation
    function animate() {
      rafRef.current = requestAnimationFrame(animate);

      // Camera snap animation (triggered by view-cube click)
      if (snapTargetRef.current) {
        const tgt = snapTargetRef.current;
        camera.position.lerp(tgt.pos, 0.1);
        if (tgt.up) camera.up.lerp(tgt.up, 0.1);
        camera.lookAt(controls.target);
        if (camera.position.distanceTo(tgt.pos) < 0.001) {
          camera.position.copy(tgt.pos);
          if (tgt.up) camera.up.copy(tgt.up);
          snapTargetRef.current = null;
        }
        controls.update();
      }

      controls.update();
      renderer.render(scene, camera);
      drawMeasureOverlay();

      // Sync & render view-cube
      if (vcRenderer && vcScene && vcCamera && vcCube) {
        vcCube.quaternion.copy(camera.quaternion).invert();
        vcRenderer.render(vcScene, vcCamera);
      }
    }
    animate();

    // Material factory
    function makeMaterial(color = 0x4fc3f7) {
      return new THREE.MeshStandardMaterial({
        color, metalness: 0.3, roughness: 0.4,
        side: THREE.DoubleSide, envMapIntensity: 0.5,
      });
    }

    // Load model
    (async function loadModel() {
      try {
        let rootObject = null, boundingBox = null;
        let totalVerts = 0, totalFaces = 0;

        if (THREE_EXTS.has(ext)) {
          if (ext === 'stl') {
            const geo = new STLLoader().parse(await fetchFileBytes(url));
            geo.computeVertexNormals(); geo.computeBoundingBox();
            const mesh = new THREE.Mesh(geo, makeMaterial());
            scene.add(mesh);
            rootObject  = mesh;
            boundingBox = geo.boundingBox.clone();
            totalVerts  = geo.attributes.position?.count || 0;
            totalFaces  = geo.index ? geo.index.count / 3 : totalVerts / 3;

          } else if (ext === 'obj') {
            const group = await new Promise((res, rej) => new OBJLoader().load(url, res, undefined, rej));
            group.traverse(c => { if (c.isMesh) c.material = makeMaterial(); });
            scene.add(group);
            rootObject  = group;
            boundingBox = new THREE.Box3().setFromObject(group);
            group.traverse(c => {
              if (c.isMesh && c.geometry) {
                totalVerts += c.geometry.attributes.position?.count || 0;
                totalFaces += c.geometry.index ? c.geometry.index.count / 3 : (c.geometry.attributes.position?.count || 0) / 3;
              }
            });

          } else if (ext === 'gltf' || ext === 'glb') {
            const gltf = await new Promise((res, rej) => new GLTFLoader().load(url, res, undefined, rej));
            scene.add(gltf.scene);
            rootObject  = gltf.scene;
            boundingBox = new THREE.Box3().setFromObject(gltf.scene);
            gltf.scene.traverse(c => {
              if (c.isMesh && c.geometry) totalVerts += c.geometry.attributes.position?.count || 0;
            });

          } else if (ext === 'ply') {
            const { PLYLoader } = await import('three/examples/jsm/loaders/PLYLoader.js');
            const geo = new PLYLoader().parse(await fetchFileBytes(url));
            geo.computeVertexNormals();
            const mesh = new THREE.Mesh(geo, makeMaterial());
            scene.add(mesh);
            rootObject  = mesh;
            boundingBox = new THREE.Box3().setFromObject(mesh);
            totalVerts  = geo.attributes.position?.count || 0;
          }

        } else if (STEP_EXTS.has(ext)) {
          setInfo({ loading: 'Parsing STEP/IGES file…' });
          const initOcct = (await import('occt-import-js')).default;
          const occt = await initOcct({ locateFile: f => f.endsWith('.wasm') ? '/occt-import-js.wasm' : f });
          const buffer = new Uint8Array(await fetchFileBytes(url));
          const isStep = ext === 'step' || ext === 'stp';
          const readFn = isStep ? 'ReadStepFile' : 'ReadIgesFile';
          if (typeof occt[readFn] !== 'function')
            throw new Error(`occt-import-js does not expose ${readFn}`);
          const result = occt[readFn](buffer, null);
          if (!result?.success) throw new Error(`${isStep ? 'STEP' : 'IGES'} parser failed`);
          const group = new THREE.Group();
          let meshCount = 0;
          for (const m of (result.meshes || [])) {
            if (!m.attributes?.position?.array?.length) continue;
            const geo = new THREE.BufferGeometry();
            geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(m.attributes.position.array), 3));
            if (m.attributes.normal?.array?.length)
              geo.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(m.attributes.normal.array), 3));
            if (m.index?.array?.length)
              geo.setIndex(new THREE.BufferAttribute(new Uint32Array(m.index.array), 1));
            if (!m.attributes.normal) geo.computeVertexNormals();
            const color = Array.isArray(m.color)
              ? new THREE.Color(m.color[0], m.color[1], m.color[2])
              : new THREE.Color(0x0e9cc0);
            group.add(new THREE.Mesh(geo, makeMaterial(color)));
            totalVerts += m.attributes.position.array.length / 3;
            meshCount++;
          }
          if (meshCount === 0) throw new Error('No geometry in STEP/IGES file');
          scene.add(group);
          rootObject  = group;
          boundingBox = new THREE.Box3().setFromObject(group);
        }

        if (!rootObject || !boundingBox) throw new Error('Unsupported or empty model');

        // Centre model
        const centre = new THREE.Vector3();
        boundingBox.getCenter(centre);
        rootObject.position.sub(centre);
        boundingBox.translate(centre.clone().negate());
        rootObjRef.current = rootObject;
        fitCamera(boundingBox);

        const size = new THREE.Vector3();
        boundingBox.getSize(size);

        // Marker radius = 0.5% of the largest dimension, min 0.01
        const maxDim = Math.max(size.x, size.y, size.z);
        markerRadiusRef.current = Math.max(maxDim * 0.005, 0.01);

        setInfo({
          verts: Math.round(totalVerts).toLocaleString(),
          faces: Math.round(totalFaces).toLocaleString(),
          size:  `${size.x.toFixed(1)} × ${size.y.toFixed(1)} × ${size.z.toFixed(1)} mm`,
          bb:    size,
        });
        setLoading(false);
      } catch (e) {
        console.error('CADViewer error:', e);
        setError(e.message || 'Failed to load model');
        setLoading(false);
      }
    })();

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      controls.dispose();
      renderer.dispose();
      if (vcRenderer) vcRenderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, ext]);

  // ── Non-viewable / viewable guard ─────────────────────────────────────────
  if (!CAD_EXTS.has(ext)) return null;

  const notViewable = !THREE_EXTS.has(ext) && !STEP_EXTS.has(ext);
  if (notViewable) {
    return (
      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: 28, textAlign: 'center', marginTop: 12 }}>
        <div style={{ fontSize: 40, marginBottom: 10 }}>📦</div>
        <div style={{ fontFamily: 'monospace', color: C.text, fontSize: 13, marginBottom: 6 }}>{filename}</div>
        <div style={{ fontFamily: 'monospace', color: C.dim, fontSize: 11, marginBottom: 18 }}>
          {ext.toUpperCase()} format cannot be previewed in-browser.
          {(ext === 'f3d' || ext === 'f3z') && ' Export as STL or STEP from Fusion 360.'}
          {ext === '3mf' && ' Export as STL to enable 3D preview.'}
        </div>
        <a href={url} target="_blank" rel="noreferrer" download
          style={{ display: 'inline-block', padding: '8px 24px', fontFamily: 'monospace', fontSize: 11, letterSpacing: 1,
            background: `${C.accent}15`, border: `1px solid ${C.accent}55`, color: C.accent, textDecoration: 'none' }}>
          ↓ DOWNLOAD {ext.toUpperCase()}
        </a>
      </div>
    );
  }

  const hasMeasurements   = measurements.length > 0;
  const hasCircles        = circleMeasurements.length > 0;
  const hasFaceDist       = faceDistMeasurements.length > 0;
  const hasAnyMeasurement = hasMeasurements || hasCircles || hasFaceDist;

  return (
    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden', marginTop: 12 }}>

      {/* ── Toolbar ── */}
      <div style={{ background: C.panel, padding: '7px 12px', display: 'flex', alignItems: 'center',
        gap: 8, flexWrap: 'wrap', borderBottom: `1px solid ${C.border}` }}>
        <span style={{ fontFamily: 'monospace', fontSize: 11, color: C.accent, flex: 1,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
          ⬡ {filename}
        </span>
        {info && !info.loading && (
          <span style={{ fontFamily: 'monospace', fontSize: 9, color: C.dim, whiteSpace: 'nowrap' }}>
            {info.verts} pts · {info.size}
          </span>
        )}
        {info?.loading && (
          <span style={{ fontFamily: 'monospace', fontSize: 9, color: C.dim }}>{info.loading}</span>
        )}

        {/* Measure toggle */}
        {!loading && !error && (
          <button
            onClick={() => { setMeasureMode(m => !m); setCircleMode(false); }}
            title="Measure distance between two points on the model"
            style={{
              background: measureMode ? '#fbbf2420' : 'transparent',
              border: `1px solid ${measureMode ? '#fbbf24' : C.border}`,
              color: measureMode ? '#fbbf24' : C.dim,
              fontFamily: 'monospace', fontSize: 9, padding: '4px 10px',
              cursor: 'pointer', letterSpacing: 1, flexShrink: 0,
            }}>
            📏 MEASURE{measureMode ? ' ON' : ''}
          </button>
        )}

        {/* Circle detect toggle */}
        {!loading && !error && (
          <button
            onClick={() => { setCircleMode(c => !c); setMeasureMode(false); setFaceDistMode(false); }}
            title="Click a circular face or cylinder to detect radius / diameter"
            style={{
              background: circleMode ? '#22d3ee20' : 'transparent',
              border: `1px solid ${circleMode ? '#22d3ee' : C.border}`,
              color: circleMode ? '#22d3ee' : C.dim,
              fontFamily: 'monospace', fontSize: 9, padding: '4px 10px',
              cursor: 'pointer', letterSpacing: 1, flexShrink: 0,
            }}>
            ⊙ CIRCLE{circleMode ? ' ON' : ''}
          </button>
        )}

        {/* Face-to-face distance toggle */}
        {!loading && !error && (
          <button
            onClick={() => { setFaceDistMode(f=>!f); setMeasureMode(false); setCircleMode(false); if(facePendingRef.current){removeSceneObjs(facePendingRef.current.markerObjs);facePendingRef.current=null;setFacePending(false);} }}
            title="Click two parallel faces to measure the perpendicular distance between them"
            style={{
              background: faceDistMode ? '#a855f720' : 'transparent',
              border: `1px solid ${faceDistMode ? '#a855f7' : C.border}`,
              color: faceDistMode ? '#a855f7' : C.dim,
              fontFamily: 'monospace', fontSize: 9, padding: '4px 10px',
              cursor: 'pointer', letterSpacing: 1, flexShrink: 0,
            }}>
            ⟂ FACE DIST{faceDistMode ? ' ON' : ''}
          </button>
        )}

        {hasAnyMeasurement && (
          <button onClick={() => { clearAllMeasurements(); clearAllCircles(); clearAllFaceDist(); }}
            style={{ background: 'transparent', border: `1px solid ${C.border}`,
              color: C.dim, fontFamily: 'monospace', fontSize: 9, padding: '4px 10px',
              cursor: 'pointer', letterSpacing: 1, flexShrink: 0 }}>
            CLEAR ALL
          </button>
        )}

        <button onClick={() => setWireframe(w => !w)}
          style={{ background: wireframe ? `${C.accent}20` : 'transparent',
            border: `1px solid ${wireframe ? C.accent : C.border}`,
            color: wireframe ? C.accent : C.dim, fontFamily: 'monospace',
            fontSize: 9, padding: '4px 10px', cursor: 'pointer', letterSpacing: 1, flexShrink: 0 }}>
          WIREFRAME
        </button>
        <button onClick={() => {
            if (rootObjRef.current) fitCamera(new THREE.Box3().setFromObject(rootObjRef.current));
          }}
          style={{ background: 'transparent', border: `1px solid ${C.border}`,
            color: C.dim, fontFamily: 'monospace', fontSize: 9, padding: '4px 10px', cursor: 'pointer', letterSpacing: 1, flexShrink: 0 }}>
          FIT
        </button>
        <a href={url} download target="_blank" rel="noreferrer"
          style={{ display: 'inline-block', background: 'transparent', border: `1px solid ${C.border}`,
            color: C.dim, fontFamily: 'monospace', fontSize: 9, padding: '4px 10px', textDecoration: 'none', letterSpacing: 1, flexShrink: 0 }}>
          ↓ DL
        </a>
      </div>

      {/* ── Measure mode hint bar ── */}
      {measureMode && !loading && !error && (
        <div style={{ background: '#fbbf2410', borderBottom: `1px solid #fbbf2430`,
          padding: '5px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#fbbf24', letterSpacing: 1 }}>
            {activePt
              ? '📍 Point 1 placed — click second point on the model'
              : '📏 Click a point on the model surface to begin measuring'}
          </span>
          {activePt && (
            <button onClick={() => {
                removeSceneObjs(measureSceneRef.current);
                measureSceneRef.current = [];
                activePtRef.current = null;
                setActivePt(null);
              }}
              style={{ background: 'transparent', border: '1px solid #fbbf2440',
                color: '#fbbf2488', fontFamily: 'monospace', fontSize: 9,
                padding: '2px 8px', cursor: 'pointer', marginLeft: 'auto' }}>
              CANCEL
            </button>
          )}
        </div>
      )}

      {/* ── Circle mode hint bar ── */}
      {circleMode && !loading && !error && (
        <div style={{ background: circleNoHit ? '#f43f5e10' : '#22d3ee10',
          borderBottom: `1px solid ${circleNoHit ? '#f43f5e44' : '#22d3ee30'}`,
          padding: '5px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 10,
            color: circleNoHit ? '#f43f5e' : '#22d3ee', letterSpacing: 1 }}>
            {circleNoHit
              ? '✕ No circular feature — click the flat top/bottom face of a cylinder or hole (not the side)'
              : '⊙ Click a flat circular face or cylindrical surface to detect radius · diameter · circumference'}
          </span>
        </div>
      )}

      {/* ── Face-dist mode hint bar ── */}
      {faceDistMode && !loading && !error && (
        <div style={{ background: faceDistMsg ? '#f43f5e10' : '#a855f710',
          borderBottom: `1px solid ${faceDistMsg ? '#f43f5e44' : '#a855f730'}`,
          padding: '5px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 10,
            color: faceDistMsg ? '#f43f5e' : '#a855f7', letterSpacing: 1 }}>
            {faceDistMsg
              ? `✕ ${faceDistMsg}`
              : facePending
              ? '⟂ Face 1 selected — now click the second parallel face'
              : '⟂ Click Face 1 (any flat surface) to begin face-to-face distance measurement'}
          </span>
        </div>
      )}

      {/* ── Canvas mount + overlay ── */}
      <div ref={mountRef} style={{ height, position: 'relative', background: '#080c14', userSelect: 'none',
        cursor: (measureMode || circleMode || faceDistMode) ? 'crosshair' : 'default' }}>
        {/* 2D overlay for measurement labels */}
        <canvas ref={overlayRef}
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 2 }} />

        {/* Orientation viewcube (bottom-right) */}
        <canvas ref={viewcubeRef}
          title="Click a face to snap to that view"
          style={{
            position: 'absolute', bottom: 10, right: 10, zIndex: 4,
            width: 110, height: 110, borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.08)',
            cursor: 'pointer',
            background: 'rgba(4,8,16,0.45)',
            backdropFilter: 'blur(4px)',
          }} />

        {loading && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 12, pointerEvents: 'none', zIndex: 3 }}>
            <div style={{ width: 32, height: 32, border: `3px solid ${C.border}`,
              borderTop: `3px solid ${C.accent}`, borderRadius: '50%', animation: 'cad-spin 1s linear infinite' }} />
            <div style={{ fontFamily: 'monospace', fontSize: 10, color: C.dim, letterSpacing: 2 }}>
              {STEP_EXTS.has(ext) ? 'PARSING STEP FILE…' : 'LOADING MODEL…'}
            </div>
            <style>{`@keyframes cad-spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
        {error && !loading && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 14, padding: 24, textAlign: 'center', zIndex: 3 }}>
            <div style={{ fontFamily: 'monospace', fontSize: 24, color: C.red }}>⚠</div>
            <div style={{ fontFamily: 'monospace', fontSize: 11, color: C.red, maxWidth: 340 }}>{error}</div>
            <a href={url} download target="_blank" rel="noreferrer"
              style={{ padding: '8px 24px', fontFamily: 'monospace', fontSize: 11, letterSpacing: 1,
                background: `${C.accent}15`, border: `1px solid ${C.accent}55`, color: C.accent, textDecoration: 'none' }}>
              ↓ DOWNLOAD FILE
            </a>
          </div>
        )}
      </div>

      {/* ── Measurement results panel ── */}
      {hasAnyMeasurement && (
        <div style={{ background: '#0a0d16', borderTop: `1px solid ${C.border}`, padding: '10px 14px',
          display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* Linear measurements */}
          {hasMeasurements && (
            <div>
              <div style={{ fontFamily: 'monospace', fontSize: 9, color: '#fbbf24', letterSpacing: 2, marginBottom: 6 }}>
                📐 DISTANCE ({measurements.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {measurements.map((m, i) => (
                  <div key={m.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 10,
                      background: C.panel, border: `1px solid ${C.border}`, borderLeft: '3px solid #fbbf24',
                      padding: '7px 12px', borderRadius: '0 4px 4px 0' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 9, color: C.dim, minWidth: 16 }}>{i+1}.</span>
                    <div style={{ flex: 1, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: '#fbbf24' }}>
                        {fmtDist(m.dist)}
                      </span>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {[['X',m.dx],['Y',m.dy],['Z',m.dz]].map(([axis,val]) => (
                          <span key={axis} style={{ fontFamily:'monospace', fontSize:9, color:C.dim }}>
                            Δ<span style={{ color: axis==='X'?'#f87171':axis==='Y'?'#4ade80':'#60a5fa' }}>{axis}</span>{' '}{fmtDist(val)}
                          </span>
                        ))}
                      </div>
                    </div>
                    <button onClick={() => clearMeasurement(m.id)} title="Remove"
                      style={{ background:'none', border:'none', color:C.dim, cursor:'pointer', fontSize:16, padding:'0 2px', lineHeight:1, flexShrink:0 }}>×</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Face-to-face distance measurements */}
          {hasFaceDist && (
            <div>
              <div style={{ fontFamily:'monospace', fontSize:9, color:'#a855f7', letterSpacing:2, marginBottom:6 }}>
                ⟂ FACE DISTANCE ({faceDistMeasurements.length})
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                {faceDistMeasurements.map((m,i)=>(
                  <div key={m.id}
                    style={{ display:'flex', alignItems:'center', gap:10,
                      background:C.panel, border:`1px solid ${C.border}`, borderLeft:'3px solid #a855f7',
                      padding:'7px 12px', borderRadius:'0 4px 4px 0' }}>
                    <span style={{ fontFamily:'monospace', fontSize:9, color:C.dim, minWidth:16 }}>{i+1}.</span>
                    <div style={{ flex:1, display:'flex', gap:20, flexWrap:'wrap', alignItems:'center' }}>
                      <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                        <span style={{ fontFamily:'monospace', fontSize:9, color:C.dim }}>FACE-TO-FACE</span>
                        <span style={{ fontFamily:'monospace', fontSize:15, fontWeight:700, color:'#a855f7' }}>
                          {fmtDist(m.dist)}
                        </span>
                      </div>
                    </div>
                    <button onClick={()=>clearFaceDist(m.id)} title="Remove"
                      style={{ background:'none', border:'none', color:C.dim, cursor:'pointer', fontSize:16, padding:'0 2px', lineHeight:1, flexShrink:0 }}>×</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Circle measurements */}
          {hasCircles && (
            <div>
              <div style={{ fontFamily: 'monospace', fontSize: 9, color: '#22d3ee', letterSpacing: 2, marginBottom: 6 }}>
                ⊙ CIRCULAR FEATURES ({circleMeasurements.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {circleMeasurements.map((m, i) => (
                  <div key={m.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 10,
                      background: C.panel, border: `1px solid ${C.border}`,
                      borderLeft: `3px solid ${m.isCylinder ? '#a78bfa' : '#22d3ee'}`,
                      padding: '7px 12px', borderRadius: '0 4px 4px 0' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 9, color: C.dim, minWidth: 16 }}>{i+1}.</span>
                    <div style={{ flex: 1, display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ fontFamily:'monospace', fontSize:8, color: m.isCylinder?'#a78bfa':'#22d3ee',
                        background: m.isCylinder?'#a78bfa18':'#22d3ee18', border:`1px solid ${m.isCylinder?'#a78bfa44':'#22d3ee44'}`,
                        padding:'1px 5px', borderRadius:2, letterSpacing:1 }}>
                        {m.isCylinder ? 'CYLINDER' : 'CIRCLE'}
                      </span>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontFamily:'monospace', fontSize:9, color:C.dim }}>RADIUS</span>
                        <span style={{ fontFamily:'monospace', fontSize:14, fontWeight:700, color: m.isCylinder?'#a78bfa':'#22d3ee' }}>
                          {fmtDist(m.radius)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontFamily:'monospace', fontSize:9, color:C.dim }}>DIAMETER</span>
                        <span style={{ fontFamily:'monospace', fontSize:14, fontWeight:700, color:'#67e8f9' }}>
                          {fmtDist(m.radius * 2)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontFamily:'monospace', fontSize:9, color:C.dim }}>CIRCUMFERENCE</span>
                        <span style={{ fontFamily:'monospace', fontSize:11, color:'#94a3b8' }}>
                          {fmtDist(m.radius * 2 * Math.PI)}
                        </span>
                      </div>
                    </div>
                    <button onClick={() => clearCircle(m.id)} title="Remove"
                      style={{ background:'none', border:'none', color:C.dim, cursor:'pointer', fontSize:16, padding:'0 2px', lineHeight:1, flexShrink:0 }}>×</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Controls hint ── */}
      <div style={{ background: C.panel, padding: '5px 12px', fontFamily: 'monospace', fontSize: 9,
        color: C.dim, letterSpacing: 1, borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4 }}>
        <span>
          {measureMode
            ? '📏 MEASURE MODE — click two points · orbit/zoom still works'
            : circleMode
            ? '⊙ CIRCLE MODE — click flat circular face / cylinder side for radius'
            : faceDistMode
            ? `⟂ FACE DIST MODE — ${facePending ? 'click Face 2' : 'click Face 1'}`
            : 'LEFT DRAG: orbit · SCROLL: zoom · RIGHT DRAG: pan'}
        </span>
        {info && !info.loading && info.verts && (
          <span style={{ color: '#334455' }}>{info.verts} vertices</span>
        )}
      </div>
    </div>
  );
}

// ─── Escape key exits measure mode ───────────────────────────────────────────
// (handled globally via the hint bar cancel button; ESC is a bonus)

/**
 * Inline CAD attachment chip shown in feeds / report cards.
 */
export function CADAttachment({ url, filename, accentColor }) {
  const [open, setOpen] = useState(false);
  const ext = getFileExt(filename);
  if (!isCADFile(filename)) return null;

  const accent   = accentColor || C.accent;
  const canView  = THREE_EXTS.has(ext) || STEP_EXTS.has(ext);

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
        background: '#0c0f18', border: `1px solid ${C.border}`, borderRadius: open ? '8px 8px 0 0' : 8 }}>
        <span style={{ fontSize: 16 }}>⬡</span>
        <span style={{ fontFamily: 'monospace', fontSize: 11, color: C.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {filename}
        </span>
        <span style={{ fontFamily: 'monospace', fontSize: 9, color: C.dim, letterSpacing: 1 }}>
          {ext.toUpperCase()}
        </span>
        {canView && (
          <button onClick={() => setOpen(o => !o)}
            style={{ background: open ? `${accent}20` : 'transparent',
              border: `1px solid ${open ? accent : C.border}`,
              color: open ? accent : C.dim, fontFamily: 'monospace', fontSize: 9,
              padding: '3px 10px', cursor: 'pointer', letterSpacing: 1, flexShrink: 0 }}>
            {open ? 'CLOSE' : 'VIEW 3D'}
          </button>
        )}
        <a href={url} download target="_blank" rel="noreferrer"
          style={{ background: 'transparent', border: `1px solid ${C.border}`,
            color: C.dim, fontFamily: 'monospace', fontSize: 9, padding: '3px 10px',
            textDecoration: 'none', letterSpacing: 1, flexShrink: 0 }}>
          ↓
        </a>
      </div>
      {open && canView && (
        <div style={{ border: `1px solid ${C.border}`, borderTop: 'none', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
          <CADViewer url={url} filename={filename} height={420} />
        </div>
      )}
    </div>
  );
}
